import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { lazy, Suspense, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiError } from '@/api/client'
import { MAP_ORDER_CAP, ordersAllQuery } from '@/api/queries'
import { FilterBar } from '@/components/FilterBar'
import { EmptyState, PageHeader } from '@/components/page'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { ordersToPoints, type MapOrder, type MapPoint } from '@/features/map/mapData'
import { PIT_DETAILS, PIT_STATES, PIT_STYLE, pitsQuery } from '@/features/map/pits'
import { AmpelBadge } from '@/features/orders/AmpelBadge'
import { AMPELN, ampelOf } from '@/features/orders/ampel'
import { orderSearchSchema, toListParams, type OrderSearch } from '@/features/orders/filters'
import { OrderFilterBar } from '@/features/orders/OrderFilterBar'
import { requireNav } from '@/nav/guard'

// MapLibre ist groß (etwa 1 MB): erst laden, wenn die Karte gebraucht wird
const PointsMap = lazy(() =>
  import('@/features/map/PointsMap').then((m) => ({ default: m.PointsMap })),
)

export const Route = createFileRoute('/_app/map')({
  beforeLoad: requireNav('map'),
  validateSearch: orderSearchSchema,
  component: MapPage,
})

function MapPage() {
  const { t } = useTranslation()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const layer = search.layer ?? 'orders'
  const update = useCallback(
    (patch: Partial<OrderSearch>) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  )

  return (
    <>
      <PageHeader title={t('pages.map.title')} description={t('pages.map.description')} />
      {/* Ebene wählen: Aufträge (Ampel) oder Gruben (Zustand) */}
      <fieldset className="mb-4 flex flex-wrap items-center gap-4">
        <legend className="mb-1 text-sm font-medium">{t('pits.layer')}</legend>
        {(['orders', 'pits'] as const).map((value) => (
          <label key={value} className="flex min-h-11 items-center gap-2">
            <input
              type="radio"
              name="map-layer"
              className="size-5"
              checked={layer === value}
              onChange={() => update({ layer: value })}
            />
            {t(value === 'orders' ? 'pits.layerOrders' : 'pits.layerPits')}
          </label>
        ))}
      </fieldset>
      {layer === 'orders' ? (
        <OrdersLayer search={search} update={update} />
      ) : (
        <PitsLayer search={search} update={update} />
      )}
    </>
  )
}

type LayerProps = { search: OrderSearch; update: (patch: Partial<OrderSearch>) => void }

function OrdersLayer({ search, update }: LayerProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [mapFailed, setMapFailed] = useState(false)
  const query = useQuery(ordersAllQuery(toListParams(search)))
  const all = useMemo(() => query.data?.items ?? [], [query.data])
  const filtered = useMemo(
    () => (search.ampel ? all.filter((o) => ampelOf(o.nextDeadlineAt) === search.ampel) : all),
    [all, search.ampel],
  )
  const located = useMemo(
    () => filtered.filter((o): o is MapOrder => Boolean(o.location)),
    [filtered],
  )
  const points = useMemo(() => ordersToPoints(located), [located])
  const withoutLocation = filtered.length - located.length

  return (
    <>
      <OrderFilterBar search={search} onChange={update} rows={all} />
      {query.isPending ? (
        <Skeleton className="h-[28rem] w-full" aria-label={t('common.loading')} />
      ) : query.isError ? (
        <EmptyState title={t('map.loadError')} body={t('errors.network')} />
      ) : (
        <>
          {query.data.truncated && (
            <p
              role="status"
              className="mb-3 rounded-md bg-warning p-3 text-sm text-warning-foreground"
            >
              {t('map.truncated', { count: MAP_ORDER_CAP })}
            </p>
          )}
          <MapOrError failed={mapFailed}>
            <PointsMap
              points={points}
              label={t('map.canvasLabel')}
              onSelect={(orderId) => void navigate({ to: '/orders/$orderId', params: { orderId } })}
              onError={() => setMapFailed(true)}
            />
          </MapOrError>
          <div
            className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2"
            aria-label={t('map.legend')}
            role="group"
          >
            <span className="text-sm font-medium">{t('map.legend')}:</span>
            {AMPELN.map((ampel) => (
              <AmpelBadge key={ampel} ampel={ampel} />
            ))}
          </div>
          {withoutLocation > 0 && (
            <p className="mt-2 text-sm text-muted-foreground">
              {t('map.noLocation', { count: withoutLocation })}
            </p>
          )}
          {/* Textalternative zur Karte: dieselben Aufträge als Liste (Tastatur, Screenreader) */}
          <section aria-labelledby="map-list" className="mt-8">
            <h2 id="map-list" className="mb-2 text-lg font-medium">
              {t('map.listTitle', { count: filtered.length })}
            </h2>
            {filtered.length === 0 ? (
              <EmptyState title={t('orders.emptyTitle')} body={t('orders.emptyBody')} />
            ) : (
              <ul className="divide-y rounded-lg border bg-card" data-testid="map-order-list">
                {filtered.map((order) => (
                  <li
                    key={order.id}
                    className="flex flex-wrap items-center justify-between gap-3 p-3"
                  >
                    <div>
                      <Link
                        to="/orders/$orderId"
                        params={{ orderId: order.id }}
                        className="inline-flex min-h-11 items-center font-medium text-primary underline"
                      >
                        {order.number}
                      </Link>
                      <p className="text-sm text-muted-foreground">{order.address}</p>
                    </div>
                    <AmpelBadge ampel={ampelOf(order.nextDeadlineAt)} />
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </>
  )
}

function MapOrError({ failed, children }: { failed: boolean; children: React.ReactNode }) {
  const { t } = useTranslation()
  return failed ? (
    <p role="alert" className="mb-3 text-destructive">
      {t('map.error')}
    </p>
  ) : (
    <Suspense fallback={<Skeleton className="h-[28rem] w-full" aria-label={t('common.loading')} />}>
      {children}
    </Suspense>
  )
}

/** D-20: Gruben nach Zustand filtern (offen, provisorisch mit fehlendem Belag, fertig), Karte und Liste. */
function PitsLayer({ search, update }: LayerProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [mapFailed, setMapFailed] = useState(false)
  const query = useQuery(pitsQuery({ state: search.pitState, detail: search.pitDetail }))
  const pits = useMemo(() => query.data ?? [], [query.data])
  const located = useMemo(() => pits.filter((p) => p.location), [pits])
  const points = useMemo<MapPoint[]>(
    () =>
      located.map((p) => ({
        id: p.orderId,
        lng: p.location?.coordinates[0] ?? 0,
        lat: p.location?.coordinates[1] ?? 0,
        color: PIT_STYLE[p.state].color,
        radius: PIT_STYLE[p.state].radius,
      })),
    [located],
  )
  const all = { value: '', label: t('common.all') }

  return (
    <>
      <FilterBar
        filters={[
          {
            id: 'pitState',
            kind: 'select',
            label: t('pits.filterState'),
            value: search.pitState ?? '',
            options: [
              all,
              ...PIT_STATES.map((s) => ({ value: s, label: t(`fieldwork.pitState.${s}`) })),
            ],
            // Der fehlende Belag gilt nur für „provisorisch“
            onChange: (v) =>
              update({
                pitState: (v || undefined) as OrderSearch['pitState'],
                pitDetail: v === 'provisorisch' ? search.pitDetail : undefined,
              }),
          },
          {
            id: 'pitDetail',
            kind: 'select',
            label: t('pits.filterDetail'),
            value: search.pitDetail ?? '',
            options: [
              all,
              ...PIT_DETAILS.map((d) => ({ value: d, label: t(`fieldwork.pitDetail.${d}`) })),
            ],
            onChange: (v) =>
              update({
                pitDetail: (v || undefined) as OrderSearch['pitDetail'],
                ...(v ? { pitState: 'provisorisch' as const } : {}),
              }),
          },
        ]}
      />
      {query.isPending ? (
        <Skeleton className="h-[28rem] w-full" aria-label={t('common.loading')} />
      ) : query.isError ? (
        <EmptyState
          title={t('pits.loadError')}
          body={
            query.error instanceof ApiError && query.error.status === 403
              ? t('forbidden.body')
              : t('errors.network')
          }
        />
      ) : (
        <>
          <MapOrError failed={mapFailed}>
            <PointsMap
              points={points}
              label={t('pits.mapLabel')}
              testId="pits-map"
              onSelect={(orderId) => void navigate({ to: '/orders/$orderId', params: { orderId } })}
              onError={() => setMapFailed(true)}
            />
          </MapOrError>
          <ul
            className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2"
            aria-label={t('map.legend')}
          >
            {PIT_STATES.map((state) => (
              <li key={state} className="flex items-center gap-2 text-sm">
                <span
                  aria-hidden="true"
                  className="inline-block rounded-full"
                  style={{
                    backgroundColor: PIT_STYLE[state].color,
                    width: PIT_STYLE[state].radius * 2,
                    height: PIT_STYLE[state].radius * 2,
                  }}
                />
                {t(`fieldwork.pitState.${state}`)}
              </li>
            ))}
          </ul>
          {pits.length > located.length && (
            <p className="mt-2 text-sm text-muted-foreground">
              {t('pits.noLocation', { count: pits.length - located.length })}
            </p>
          )}
          <section aria-labelledby="pit-list" className="mt-8">
            <h2 id="pit-list" className="mb-2 text-lg font-medium">
              {t('pits.listTitle', { count: pits.length })}
            </h2>
            {pits.length === 0 ? (
              <EmptyState title={t('pits.empty')} />
            ) : (
              <ul className="divide-y rounded-lg border bg-card" data-testid="pit-list">
                {pits.map((pit) => (
                  <li
                    key={pit.id}
                    className="flex flex-wrap items-center justify-between gap-3 p-3"
                    data-pit-state={pit.state}
                  >
                    <div>
                      <span className="font-medium">{pit.label}</span>{' '}
                      <Link
                        to="/orders/$orderId"
                        params={{ orderId: pit.orderId }}
                        className="inline-flex min-h-11 items-center text-primary underline"
                      >
                        {pit.orderNumber}
                      </Link>
                    </div>
                    <span className="flex flex-wrap items-center gap-2">
                      <Badge variant="secondary">{t(`fieldwork.pitState.${pit.state}`)}</Badge>
                      {(pit.details ?? []).map((detail) => (
                        <Badge key={detail} variant="outline">
                          {t(`fieldwork.pitDetail.${detail}`)}
                        </Badge>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </>
  )
}
